import { useCallback, useEffect, useRef, useState } from 'react';
import PageTemplate from 'components/template/PageTemplate';
import Container from 'components/common/Container';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jsonAxios } from 'components/axios';
import { toast } from 'react-toastify';
import { countryCodeToEmoji, download } from 'components/function';
import { ReactComponent as Down } from 'assets/special/down.svg'
import CustomButton from 'components/common/CustomButton';
import { ReactComponent as Nodata } from 'assets/common/quoteNodata.svg';
import { hsCodeResultAtom } from 'atom/hsCode';
import { useSetAtom } from 'jotai';
import useCheckCompany from 'hook/useCheckCompany';
import { IOption, IStatusDetail, ITotal, SelectedOptionsType } from 'types/quote';
import { ReactComponent as Frozen } from 'assets/quote/frozen.svg'
import { ReactComponent as Danger } from 'assets/quote/danger.svg'
import { ReactComponent as CheckOn } from 'assets/common/checkOn.svg'
import { ReactComponent as CheckOff } from 'assets/common/checkOff.svg'
import QuoteDetail from 'components/common/modal/QuoteDetail';
import Dialog from 'components/common/Dialog';
import { ReactComponent as FileList } from 'assets/common/fileList.svg'
import QuoteDone from 'components/common/modal/QuoteDone';
import QuoteConfrim from 'components/common/modal/QuoteConfrim';

const StatusDetail = () => {
    interface IFile {
        id: number
        fileName: string
        filePath: string
    }
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get('id');
    const handledNoId = useRef<boolean>(false);
    const [data, setData] = useState<IStatusDetail>({} as IStatusDetail);
    const [total, setTotal] = useState<ITotal[]>([]);
    const [detailInfo, setDetailInfo] = useState({
        quoteId: 0,
        optionId: 0,
    })

    const [popDangerListOpen, setPopDangerListOpen] = useState<boolean>(false);
    const [dangerFileArr, setDangerFileArr] = useState<IFile[]>([]);
    const [popOpenDetail, setPopOpenDetail] = useState<boolean>(false)
    const tHeader = [
        { label: "No.", flex: 1, type: '' },
        { label: "CNTR", flex: 1, type: '' },
        { label: "G.Weight", flex: 1, type: '' },
        { label: "수량", flex: 1, type: '' },
        { label: "FLEXI BAG", flex: 1, type: 'FCL' },
        { label: "냉장/냉동", flex: 1, type: 'FCL' },
        { label: "위험물", flex: 1, type: 'FCL' },
    ];

    const optionTHeader = [
        { label: '확정', flex: 0.5 },
        { label: '운송견적명', flex: 1 },
        { label: '선사/항공명', flex: 1.3 },
        { label: '합계', flex: 1 },
    ]

    const setHsCodeResult = useSetAtom(hsCodeResultAtom);
    const [clear, setClear] = useState<boolean>(false);
    const [popOpenDone, setPopOpenDone] = useState<boolean>(false)
    const [popOpen, setPopOpen] = useState<boolean>(false)
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptionsType>({});
    const [orderId, setOrderId] = useState<number>(0)
    useCheckCompany();

    const init = useCallback(() => {
        jsonAxios.get(`quote/${id}`).then(res => {
            if (res.data.code === 200) {
                setData(res.data.data)
                if (res.data.data.status === 'COMPLETE' || res.data.data.status === 'CANCELED') {
                    setOrderId(res.data.data.orderId)
                    let selectedOnly: SelectedOptionsType = {};

                    if (res.data.data.shipmentMode?.includes('FCL') || res.data.data.shipmentMode?.includes('OT')) {
                        selectedOnly = res.data.data.containerInfo.reduce((acc: SelectedOptionsType, container: any) => {
                            const selectedOption = container.optionInfo.find((opt: IOption) => opt.isSelected);
                            if (selectedOption) {
                                acc[container.containerId] = selectedOption;
                            }
                            return acc;
                        }, {});
                    }
                    else {
                        selectedOnly = res.data.data.optionInfo
                            .filter((opt: IOption) => opt.isSelected)
                            .reduce((acc: SelectedOptionsType, opt: IOption) => {
                                acc[opt.id] = opt;
                                return acc;
                            }, {});
                    }

                    setSelectedOptions(selectedOnly);
                }
            }
        }).catch((e) => {
            console.log(e)
            toast.error('서버 오류가 발생했습니다.');
        });
    }, [id]);

    useEffect(() => {
        if (!id) {
            if (!handledNoId.current) {
                handledNoId.current = true;
                toast.error('잘못된 경로입니다.');
                setTimeout(() => {
                    navigate(-1);
                }, 500);
            }
            return;
        }
        setHsCodeResult('');
        init();
    }, [id, init]);

    useEffect(() => {
        setTotal(calcTotalCurrency(selectedOptions))

        if (data.transportType === 'SEA' && (data?.shipmentMode?.includes('FCL') || data?.shipmentMode?.includes('OT'))) {
            if (data?.containerInfo?.length > 0 && (data?.containerInfo?.length === Object.entries(selectedOptions)?.length)) {
                setClear(true)
            }
            else {
                setClear(false)
            }
        }
        else {
            if (data?.optionInfo?.length > 0 && (Object.entries(selectedOptions)?.length > 0)) {
                setClear(true)
            }
            else {
                setClear(false)
            }
        }
    }, [selectedOptions])

    const calcTotalCurrency = (selectedOptions: SelectedOptionsType): ITotal[] => {
        // Map<currency, totalPrice>
        const map = new Map<string, number>();

        Object.values(selectedOptions).forEach(option => {
            if (!option.totalPrice) return;

            const prices = convertTotalPrice(option.totalPrice); // ITotal[] 배열
            prices.forEach(({ currency, price }) => {
                if (!currency || price == null || isNaN(price)) return;
                map.set(currency, (map.get(currency) || 0) + price);
            });
        });

        const priority = ['USD', 'KRW', 'EUR'];

        return Array.from(map.entries())
            .map(([currency, price]) => ({ currency, price }))
            .sort((a, b) => {
                const indexA = priority.indexOf(a.currency.toUpperCase());
                const indexB = priority.indexOf(b.currency.toUpperCase());
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
    };

    const openDetailPop = (quoteId: number, optionId: number) => {
        setDetailInfo({
            quoteId: quoteId,
            optionId: optionId,
        })
        setPopOpenDetail(true)
    }

    const convertTotalPrice = (price: string): ITotal[] => {
        if (price) {
            const validJson = price.replace(/'/g, '"');
            const parsed: ITotal[] = JSON.parse(validJson);
            return parsed ? parsed.filter(arr => !arr.currency.includes('Cost')) : []
        }
        else {
            return []
        }
    }

    const tBodyStyle = () => {
        return `flex items-center justify-center py-2 text-center tablet:py-0 tablet:gap-2`
    }

    const titleStyle = () => {
        return `hidden tablet:block flex-[1.0] w-full bg-[#F8F8FA] py-1`
    }

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 mb-12 tablet:py-4 pt-21 h-full min-h-dvh">
                <p className='font-semibold tablet:text-lg text-3xl'>견적상세</p>
                <div className='bg-white border-[#9D9EB7] border-1 rounded-lg'>
                    <div className='flex items-center gap-6 tablet:gap-4 tablet:p-4 px-10 py-6 border-[#F8F8FA] border-b font-semibold'>
                        <p className='tablet:text-lg text-2xl'>견적내역</p>
                        <div className='flex tablet:flex-1 justify-center items-center bg-[#EEF4FF] rounded w-[240px] h-10'>
                            <p className='tablet:text-sm text-lg'>{data.username}</p>
                        </div>
                    </div>

                    {
                        data &&
                        data.destinationCode &&
                        <div className='relative flex 2xl:flex-row flex-col text-sm'>
                            <div className='flex flex-col flex-1 gap-6 tablet:gap-4 tablet:p-4 px-10 py-6'>
                                {
                                    data.containerInfo.map((item, idx) => (
                                        <div className='flex flex-col gap-3' key={idx}>
                                            <div className='flex items-center gap-2'>
                                                <p className='tablet:text-base text-lg'>컨테이너 정보</p>
                                                <p className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 text-white text-xs'>{idx + 1}</p>
                                            </div>
                                            <div className='tablet:hidden flex bg-[#EEF4FF]'>
                                                {
                                                    tHeader
                                                        .filter(items => item.shipmentMode !== 'OT' || items.type !== 'FCL')
                                                        .map((item, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`flex-[${item.flex}] flex items-center justify-center h-10`}
                                                            >
                                                                {item.label}
                                                            </div>
                                                        ))
                                                }
                                            </div>
                                            <div
                                                className={`flex h-15 border border-[#E1E1E8] rounded tablet:gap-1 tablet:flex-col tablet:h-auto`}
                                                key={idx}>
                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                    <p className={titleStyle()}>{tHeader[0].label}</p>
                                                    <p className='flex-1'>
                                                        {idx + 1}
                                                    </p>
                                                </div>
                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                    <p className={titleStyle()}>{tHeader[1].label}</p>
                                                    <p className='flex-1'>
                                                        {item.containerType}
                                                    </p>
                                                </div>
                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                    <p className={titleStyle()}>{tHeader[2].label}</p>
                                                    <p className='flex-1'>
                                                        {Number(item.containerWeight).toLocaleString()} Kg
                                                    </p>
                                                </div>
                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                    <p className={titleStyle()}>{tHeader[3].label}</p>
                                                    <p className='flex-1'>
                                                        {item.containerQuantity}
                                                    </p>
                                                </div>

                                                {
                                                    item.shipmentMode !== 'OT' &&
                                                    <>
                                                        <div className={`flex-[1] ${tBodyStyle()}`}>
                                                            <p className={titleStyle()}>{tHeader[4].label}</p>
                                                            <p className='flex-1'>
                                                                {item.isFlexibag !== undefined && (item.isFlexibag ? '예' : '아니요')}
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[1] ${tBodyStyle()}`}>
                                                            <p className={titleStyle()}>{tHeader[5].label}</p>
                                                            <p className='flex flex-col flex-1 justify-center items-center'>
                                                                <Frozen fill={item.isFrozen ? '#196BFF' : '#9D9EB7'} />
                                                                {
                                                                    item.isFrozen &&
                                                                    <p className='text-center leading-none'>
                                                                        {item.frozenTempMax}°C ~ {item.frozenTempMax}°C
                                                                    </p>
                                                                }
                                                                {
                                                                    item.isFrozen && item.isVent !== undefined &&
                                                                    <p className='text-center leading-none'>
                                                                        Vent: {item.isVent ? "Open" : "Close"}
                                                                    </p>
                                                                }
                                                            </p>
                                                        </div>
                                                        <div className={`flex-[1] ${tBodyStyle()} ${item.isDangerous && 'cursor-pointer'}`}
                                                            onClick={() => {
                                                                if (item.isDangerous) {
                                                                    setPopDangerListOpen?.(true)
                                                                    setDangerFileArr?.(item.fileInfo)
                                                                }
                                                            }}
                                                        >
                                                            <p className={titleStyle()}>{tHeader[6].label}</p>
                                                            <p className='flex flex-col flex-1 justify-center items-center'>
                                                                <Danger fill={item.isDangerous ? '#FF0000' : '#9D9EB7'} />
                                                            </p>
                                                        </div>
                                                    </>
                                                }
                                            </div>

                                            {/* 옵션 */}
                                            {
                                                item.optionInfo &&
                                                    item.optionInfo.length > 0 ?
                                                    <>
                                                        <div className='flex items-center gap-2'>
                                                            <p className='tablet:text-base text-lg'>견적 선택</p>
                                                            <p className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 text-white text-xs'>{idx + 1}</p>
                                                        </div>
                                                        <div className='tablet:hidden flex bg-[#EEF4FF] text-center'>
                                                            {
                                                                optionTHeader.map((header, idx) => (
                                                                    <p
                                                                        key={idx}
                                                                        className={`flex-[${header.flex}] p-3`}
                                                                    >
                                                                        {header.label}
                                                                    </p>
                                                                ))
                                                            }
                                                        </div>
                                                        {item.optionInfo.map((option, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`flex border border-[#E1E1E8] rounded text-sm text-center cursor-pointer hover:bg-[#EEF4FF] tablet:flex-col tablet:gap-1 tablet:hover:bg-white`}
                                                                onClick={() => openDetailPop(data.id, option.id)}
                                                            >
                                                                {/* 확정 영역 */}
                                                                <div className={`flex-[0.5] ${tBodyStyle()} p-3 tablet:p-0`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (data.status === 'COMPLETE') return
                                                                        if (data.status === 'CANCELED') return
                                                                        setSelectedOptions(prev => {
                                                                            const currentSelected = prev[item.containerId];
                                                                            if (currentSelected?.id === option.id) {
                                                                                const { [item.containerId]: _, ...rest } = prev;
                                                                                return rest;
                                                                            } else {
                                                                                return {
                                                                                    ...prev,
                                                                                    [item.containerId]: option,
                                                                                };
                                                                            }
                                                                        });
                                                                    }}
                                                                >
                                                                    <p className={titleStyle()}>{optionTHeader[0].label}</p>
                                                                    <div className='flex flex-1 justify-center'>
                                                                        {
                                                                            (data.status === 'COMPLETE' || data.status === 'CANCELED') ?
                                                                                option.isSelected ?
                                                                                    <CheckOn className="w-6 tablet:w-5" /> :
                                                                                    <CheckOff className="w-6 tablet:w-5" /> :
                                                                                selectedOptions[item.containerId]?.id === option.id ?
                                                                                    <CheckOn className="w-6 tablet:w-5" /> :
                                                                                    <CheckOff className="w-6 tablet:w-5" />
                                                                        }
                                                                    </div>
                                                                </div>
                                                                {/* 옵션명 */}
                                                                <div className={`flex-[1] ${tBodyStyle()} p-3 tablet:p-0`}>
                                                                    <p className={titleStyle()}>{optionTHeader[1].label}</p>
                                                                    <p className='flex-1'>
                                                                        {option.optionName}
                                                                    </p>
                                                                </div>

                                                                {/* 선사명 */}
                                                                <div className={`flex-[1.3] ${tBodyStyle()} min-w-0 p-3 tablet:p-0`}>
                                                                    <p className={titleStyle()}>{optionTHeader[2].label}</p>
                                                                    <p className='flex-1 truncate'>
                                                                        {option.carrierName}
                                                                    </p>
                                                                </div>

                                                                {/* 가격 */}
                                                                <div className={`flex-[1.0] ${tBodyStyle()} p-3 tablet:p-0`}>
                                                                    <p className={titleStyle()}>{optionTHeader[3].label}</p>
                                                                    <div className="flex flex-col flex-1 gap-1 text-left">
                                                                        {convertTotalPrice(option.totalPrice).map((item, idx) => (
                                                                            <div className="flex justify-center gap-3" key={idx}>
                                                                                <p className="w-10">{item.currency}</p>
                                                                                <p>{item.price.toLocaleString()}</p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                        )}
                                                    </> :
                                                    <div className='flex justify-center items-center gap-3 py-10 w-full text-lg'>
                                                        <Nodata />
                                                        해당 견적요청은 등록된 견적서가 없습니다.
                                                    </div>
                                            }
                                            {
                                                data.containerInfo.length - 1 !== idx &&
                                                <hr className='my-3' />
                                            }
                                        </div>
                                    ))
                                }
                                {/* 옵션 */}
                                {
                                    ((data.transportType === 'AIR') || (!data.shipmentMode?.includes('FCL') && !data.shipmentMode?.includes('OT'))) &&
                                    <>
                                        {
                                            data.optionInfo &&
                                                data.optionInfo.length > 0 ?
                                                <div className='flex flex-col gap-2'>
                                                    <div className='flex items-center gap-2'>
                                                        <p className='text-lg'>견적 선택</p>
                                                        {/* <p className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 text-white text-xs'>{idx + 1}</p> */}
                                                    </div>
                                                    <div className='tablet:hidden flex bg-[#EEF4FF] text-center'>
                                                        {
                                                            optionTHeader.map((header, idx) => (
                                                                <p
                                                                    key={idx}
                                                                    className={`flex-[${header.flex}] p-3`}
                                                                >
                                                                    {header.label}
                                                                </p>
                                                            ))
                                                        }
                                                    </div>
                                                    {data.optionInfo.map((option, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`flex items-center border border-[#E1E1E8] rounded text-sm text-center cursor-pointer hover:bg-[#EEF4FF]`}
                                                            onClick={() => openDetailPop(data.id, option.id)}
                                                        >
                                                            {/* 확정 영역 */}
                                                            <div
                                                                className={`flex-[0.5] flex items-center justify-center p-3`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (data.status === 'COMPLETE') return
                                                                    if (data.status === 'CANCELED') return
                                                                    setSelectedOptions(prev => {
                                                                        const currentSelected = prev[data.id];
                                                                        if (currentSelected?.id === option.id) {
                                                                            const { [data.id]: _, ...rest } = prev;
                                                                            return rest;
                                                                        } else {
                                                                            return {
                                                                                ...prev,
                                                                                [data.id]: option,
                                                                            };
                                                                        }
                                                                    });
                                                                }}
                                                            >
                                                                {
                                                                    (data.status === 'COMPLETE' || data.status === 'CANCELED') ?
                                                                        option.isSelected ?
                                                                            <CheckOn className="w-[24px]" /> :
                                                                            <CheckOff className="w-[24px]" /> :
                                                                        selectedOptions[data.id]?.id === option.id ?
                                                                            <CheckOn className="w-[24px]" /> :
                                                                            <CheckOff className="w-[24px]" />
                                                                }
                                                            </div>

                                                            {/* 옵션명 */}
                                                            <p className="flex flex-[1] justify-center items-center p-3">
                                                                {option.optionName}
                                                            </p>

                                                            {/* 선사명 */}
                                                            <p className="flex flex-[1.3] justify-center items-center p-3">
                                                                {option.carrierName}
                                                            </p>

                                                            {/* 가격 */}
                                                            <div className="flex flex-[1] justify-center items-center p-3">
                                                                <div className="flex flex-col gap-1 text-left">
                                                                    {convertTotalPrice(option.totalPrice).map((item, idx) => (
                                                                        <div className="flex justify-between gap-3" key={idx}>
                                                                            <p className="w-10">{item.currency}</p>
                                                                            <p>{item.price.toLocaleString()}</p>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                    )}
                                                </div> :
                                                <div className='flex justify-center items-center gap-3 py-20 w-full text-lg'>
                                                    <Nodata />
                                                    해당 견적요청은 등록된 견적서가 없습니다.
                                                </div>
                                        }
                                    </>
                                }
                            </div>

                            <div className='top-18 sticky flex-1 tablet:p-4 px-10 py-6 rounded-xl h-full'>
                                <div className='flex flex-col gap-9 tablet:gap-4 bg-white'>
                                    <div className='border border-[#9D9EB7] rounded-lg'>
                                        <div className='px-6 py-4 border-[#F8F8FA] border-b tablet:text-lg text-2xl'>
                                            <span className='font-medium'>{data.quoteId}</span>
                                        </div>
                                        <div className='flex flex-col justify-between items-start gap-4 tablet:p-4 px-6 py-4'>
                                            <div className='flex flex-1 items-center gap-2'>
                                                <p className='w-10 text-42 flag'>{countryCodeToEmoji(data.originCode.slice(0, 2))}</p>
                                                <div className='tablet:text-base text-lg'>
                                                    <p className='font-semibold leading-none'>{data.originCode}</p>
                                                    <p className='font-semibold leading-none'>{data.originAddress}</p>
                                                    {
                                                        data.etd &&
                                                        <p className='text-base'>{data.etd}(ETD)</p>
                                                    }
                                                </div>
                                            </div>
                                            <Down className='self-center' />
                                            <div className='flex flex-1 items-center gap-2'>
                                                <p className='w-10 text-42 flag'>{countryCodeToEmoji(data.destinationCode.slice(0, 2))}</p>
                                                <div className='tablet:text-base text-lg'>
                                                    <p className='font-semibold leading-none'>{data.destinationCode}</p>
                                                    <p className='font-semibold leading-none'>{data.destinationAddress}</p>
                                                    {
                                                        data.eta &&
                                                        <p className='text-base'>{data.eta}(ETA)</p>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* 선택한견적들 */}
                                    {
                                        selectedOptions && Object.entries(selectedOptions).length > 0 && (
                                            <div className="p-6 tablet:p-4 border border-[#9D9EB7] rounded-lg">
                                                <div className="mb-6 font-semibold tablet:text-lg text-2xl">
                                                    확정 견적
                                                </div>

                                                <div className='flex flex-col gap-2'>
                                                    <div className="tablet:hidden flex items-center bg-[#EEF4FF] rounded h-10">
                                                        <div className="flex-[0.5] text-center">순서</div>
                                                        <div className="flex-[1] text-center">운송견적명</div>
                                                        <div className="flex-[1.5] text-center">선사/항공명</div>
                                                        <div className="flex-[1] text-center">합계</div>
                                                    </div>

                                                    {Object.entries(selectedOptions)
                                                        .sort(([a], [b]) => Number(a) - Number(b))  // containerId 기준 오름차순 정렬
                                                        .map(([containerId, option], index) => (
                                                            <div
                                                                key={containerId}
                                                                className="flex tablet:flex-col tablet:gap-1 border border-[#E1E1E8] rounded h-15 tablet:h-auto text-center"
                                                            >
                                                                <div className={`flex-[0.5] ${tBodyStyle()}`}>
                                                                    <p className={titleStyle()}>순서</p>
                                                                    <div className='flex flex-1 justify-center items-center'>
                                                                        <p className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 text-white text-xs'>{index + 1}</p>
                                                                    </div>
                                                                </div>
                                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                                    <p className={titleStyle()}>운송견적명</p>
                                                                    <p className='flex-1 truncate'>
                                                                        {option.optionName}
                                                                    </p>
                                                                </div>
                                                                <div className={`flex-[1.5] min-w-0 ${tBodyStyle()}`}>
                                                                    <p className={titleStyle()}>선사/항공명</p>
                                                                    <p className='flex-1 truncate'>
                                                                        {option.carrierName}
                                                                    </p>
                                                                </div>
                                                                <div className={`flex-[1] ${tBodyStyle()}`}>
                                                                    <p className={titleStyle()}>합계</p>
                                                                    <p className='flex-1'>
                                                                        {convertTotalPrice(option.totalPrice).map((item, idx) => (
                                                                            <div className="flex justify-center gap-3" key={idx}>
                                                                                <p className="w-10">{item.currency}</p>
                                                                                <p>{item.price.toLocaleString()}</p>
                                                                            </div>
                                                                        ))}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )
                                    }

                                    {
                                        total.length > 0 &&
                                        <div className='flex tablet:flex-col justify-between items-end tablet:items-start gap-3 py-6 tablet:py-2 border-[#D9D9D9] border-b'>
                                            <p className='font-semibold tablet:text-lg text-2xl'>
                                                합계
                                            </p>
                                            <div className='flex gap-5'>
                                                {
                                                    total.map((item, idx) => (
                                                        <div
                                                            className='flex gap-1 rounded font-semibold tablet:text-base text-xl'
                                                            key={idx}>
                                                            <p className='text-primary'>{item.currency}</p>
                                                            <p>{item.price?.toLocaleString()}</p>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    }

                                    {
                                        !(data.status === 'COMPLETE' || data.status === 'CANCELED') &&
                                        <div className='py-6 tablet:py-0'>
                                            {
                                                clear &&
                                                <CustomButton
                                                    className='bg-primary w-full'
                                                    title='견적 확정'
                                                    onClick={() => {
                                                        setPopOpen(true)
                                                    }}
                                                />
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
                {
                    data.status === 'COMPLETE' &&
                    <CustomButton
                        className='self-end bg-primary !w-[180px]'
                        title='진행현황'
                        onClick={() => {
                            if (orderId > 0) {
                                navigate(`/transport/detail?id=${orderId}`)
                            }
                        }}
                    />
                }
            </Container>

            <Dialog
                isOpen={popDangerListOpen}
                setIsOpen={setPopDangerListOpen}
                btnText='닫기'
            >
                {
                    dangerFileArr.map((item, idx) => (
                        <div
                            className='relative flex items-center gap-3 mb-3 px-6 border border-primary rounded h-10 text-primary tablet:text-xs text-sm cursor-pointer'
                            key={idx}
                            onClick={() => download(item.filePath)}>
                            <FileList />
                            {item.fileName}
                        </div>
                    ))
                }
            </Dialog>

            <QuoteConfrim
                isOpen={popOpen}
                quoteId={id}
                data={data}
                setIsOpen={setPopOpen}
                setOpenDone={setPopOpenDone}
                selectedOptions={selectedOptions}
            />

            <QuoteDetail
                isOpen={popOpenDetail}
                setIsOpen={setPopOpenDetail}
                select={detailInfo}
            />

            <QuoteDone
                isOpen={popOpenDone}
                setIsOpen={setPopOpenDone}
                refresh={init}
            />

        </PageTemplate>
    )
}

export default StatusDetail